/**
 * Every dialog and drawer opens with the cursor in its first field (#380 U25,
 * documentation/ui-qa/forms/dialog-initial-focus-acceptance.md, FORM-FOCUS-001 to 003).
 *
 * WHY. On open, Shoelace focuses a light-DOM element marked `autofocus`, and otherwise the dialog's
 * own panel, so a person has to click into the first field before typing (the architect: "they
 * should not have to click into the edit field"). Two dialogs mark a field today; every other one
 * opened on its panel, and one mounted already open took no focus at all (rule 5).
 *
 * WHY AT THE DOCUMENT, once (rule 1). Shoelace emits a cancelable `sl-initial-focus` from the dialog
 * or drawer one frame after it opens, and focuses its default only when nobody prevents it. One
 * capture-phase listener on the document hears it from every dialog in the app, including those
 * inside other components' shadow roots, so a dialog written next year is covered without anyone
 * remembering this rule. The same bootstrap point and reasoning as the number-input wheel guard.
 *
 * THE RULES IT KEEPS:
 * - A dialog that names its own field keeps it (rule 2). Shoelace removes `autofocus` from its
 *   target before emitting `sl-initial-focus` and puts it back afterwards, so the mark is read on
 *   `sl-show`, which Shoelace emits first, synchronously, while the attribute is still there.
 * - A dialog with no field is left alone (rule 3): the event is not prevented, so Shoelace focuses
 *   its panel exactly as before.
 * - Content that renders after opening is waited for (rule 4): when no field is there yet, Shoelace
 *   is left to focus its panel, then the dialog's nested Lit elements are awaited once and one frame
 *   passes, and the first field found then gets the cursor, provided focus is still on the dialog
 *   itself. Bounded: one wait, one search, no polling.
 * - A dialog whose fields wait on a server answer is the one exception (rule 4, SA 2026-09-28): the
 *   bounded wait ends before the server answers, so such a dialog calls
 *   `focusFirstFieldIfDialogStillFocused` itself, once, when its content lands. Same finder, same
 *   "only while focus is still on the dialog" guard; nothing polls.
 * - A dialog already open when it first renders gets the same decision (rule 5). Shoelace decides
 *   focus only when `open` changes after the first render, so a dialog mounted open emits neither
 *   event and used to leave focus wherever it was. A Lit initializer on Shoelace's dialog and
 *   drawer classes hears that first render instead, and decides as Shoelace's own open path and
 *   this listener would have together: its marked field, else its first field, else its panel.
 *   Like Shoelace's open path it remembers where focus was and gives it back when the dialog
 *   closes, or when its host removes it while still open.
 */

import type { ReactiveElement } from 'lit';
import SlDialog from '@shoelace-style/shoelace/dist/components/dialog/dialog.component.js';
import SlDrawer from '@shoelace-style/shoelace/dist/components/drawer/drawer.component.js';

/** The overlays whose opening focus this rule decides. */
const OVERLAY_TAGS = new Set(['sl-dialog', 'sl-drawer']);

/** Controls a person types in or picks from. Buttons and checkboxes are not a "first edit". */
const FIELD_TAGS = new Set([
  'sl-input',
  'sl-textarea',
  'sl-select',
  'sl-combobox',
  'input',
  'textarea',
  'select',
]);

/** Native input types that are not typed in or picked from. */
const NON_FIELD_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** Decides whether an element is on screen; injectable because happy-dom lays nothing out. */
export type VisibilityCheck = (element: Element) => boolean;

/**
 * On screen as the browser renders it: `checkVisibility` where the browser has it (it sees
 * display:none and visibility:hidden on any ancestor, across shadow boundaries), otherwise the
 * element having any layout box at all.
 */
export const isRendered: VisibilityCheck = (element) => {
  const check = (
    element as Element & {
      checkVisibility?: (options?: { visibilityProperty?: boolean }) => boolean;
    }
  ).checkVisibility;
  if (typeof check === 'function') {
    return check.call(element, { visibilityProperty: true });
  }
  return element.getClientRects().length > 0;
};

/**
 * The first field in `root`'s light content that a person can type in or pick from, in document
 * order, or null. It walks each element's open shadow root before its light children, which is the
 * order a component's own fields render around its slot in every form component in this app, and
 * never walks into a field it has matched (an sl-input's inner input is that sl-input). The root's
 * OWN shadow root is not walked: for a dialog that holds only its panel, header and close button.
 *
 * A field counts when it is enabled, not read-only, and visible; a native input only when its type
 * is typed in or picked from.
 */
export function findFirstEditableField(
  root: Element,
  isVisible: VisibilityCheck = isRendered
): HTMLElement | null {
  for (const child of Array.from(root.children)) {
    const found = searchElement(child, isVisible);
    if (found) return found;
  }
  return null;
}

function searchElement(
  element: Element,
  isVisible: VisibilityCheck
): HTMLElement | null {
  if (FIELD_TAGS.has(element.localName)) {
    return isUsableField(element, isVisible) ? (element as HTMLElement) : null;
  }
  // A dialog nested inside this one decides its own focus when it opens.
  if (OVERLAY_TAGS.has(element.localName)) return null;
  if (element.shadowRoot) {
    for (const child of Array.from(element.shadowRoot.children)) {
      const found = searchElement(child, isVisible);
      if (found) return found;
    }
  }
  for (const child of Array.from(element.children)) {
    const found = searchElement(child, isVisible);
    if (found) return found;
  }
  return null;
}

/** Enabled, not read-only, a typed-in or picked-from kind, and on screen. */
function isUsableField(element: Element, isVisible: VisibilityCheck): boolean {
  const field = element as HTMLElement & {
    disabled?: boolean;
    readonly?: boolean;
    readOnly?: boolean;
    type?: string;
  };
  if (field.disabled || element.hasAttribute('disabled')) return false;
  if (field.readonly || field.readOnly || element.hasAttribute('readonly')) {
    return false;
  }
  if (
    element.localName === 'input' &&
    NON_FIELD_INPUT_TYPES.has((field.type ?? '').toLowerCase())
  ) {
    return false;
  }
  return isVisible(element);
}

/** The dialog or drawer an event was emitted by, or null for anything else. */
function overlayOf(event: Event): HTMLElement | null {
  const origin =
    typeof event.composedPath === 'function'
      ? event.composedPath()[0]
      : event.target;
  return origin instanceof HTMLElement && OVERLAY_TAGS.has(origin.localName)
    ? origin
    : null;
}

/** Every element under `root`, light and open shadow, that is a Lit element still updating. */
function pendingUpdates(root: Element): Promise<unknown>[] {
  const pending: Promise<unknown>[] = [];
  const visit = (element: Element) => {
    const update = (element as Element & { updateComplete?: unknown })
      .updateComplete;
    if (update instanceof Promise) pending.push(update);
    if (element.shadowRoot) {
      for (const child of Array.from(element.shadowRoot.children)) visit(child);
    }
    for (const child of Array.from(element.children)) visit(child);
  };
  for (const child of Array.from(root.children)) visit(child);
  return pending;
}

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Installed once at bootstrap. Returns a disposer, which the tests use. `isVisible` is for the
 * tests, whose DOM has no layout.
 */
export function installDialogInitialFocus(
  target: Document = document,
  isVisible: VisibilityCheck = isRendered
): () => void {
  // Overlays opened with their own marked field, as read on sl-show before Shoelace strips it.
  const namesItsOwnField = new WeakSet<HTMLElement>();

  const onShow = (event: Event) => {
    const overlay = overlayOf(event);
    if (!overlay) return;
    if (overlay.querySelector('[autofocus]')) namesItsOwnField.add(overlay);
    else namesItsOwnField.delete(overlay);
  };

  const onInitialFocus = (event: Event) => {
    const overlay = overlayOf(event);
    if (!overlay || namesItsOwnField.has(overlay)) return;
    const field = findFirstEditableField(overlay, isVisible);
    if (field) {
      event.preventDefault();
      field.focus({ preventScroll: true });
      return;
    }
    void focusFirstFieldIfDialogStillFocused(overlay, target, isVisible);
  };

  target.addEventListener('sl-show', onShow, { capture: true });
  target.addEventListener('sl-initial-focus', onInitialFocus, {
    capture: true,
  });
  const decide = (overlay: HTMLElement) =>
    decideOnFirstRender(overlay, target, isVisible);
  firstRenderDecision = decide;
  return () => {
    target.removeEventListener('sl-show', onShow, { capture: true });
    target.removeEventListener('sl-initial-focus', onInitialFocus, {
      capture: true,
    });
    if (firstRenderDecision === decide) firstRenderDecision = null;
  };
}

/**
 * What rule 5 runs for a dialog or drawer that was open on its first render, or null while no
 * installer is live. Lit initializers cannot be removed once added, so they are added once, below,
 * and call through this, which the installer sets and its disposer clears.
 */
let firstRenderDecision: ((overlay: HTMLElement) => void) | null = null;

/**
 * Lets every Shoelace dialog and drawer constructed from now on report a first render with `open`
 * already true. Added when this module is evaluated rather than when the installer runs, so it
 * reaches every dialog main.ts's later imports could construct; the decision itself waits for the
 * installer. It waits one frame, as Shoelace's own open path does, so the dialog's `firstUpdated`
 * has unhidden it and its content has rendered.
 */
const reportFirstRenderOpen = (element: ReactiveElement) => {
  let firstUpdateSeen = false;
  element.addController({
    hostUpdated() {
      if (firstUpdateSeen) return;
      firstUpdateSeen = true;
      if (!(element as ReactiveElement & { open?: boolean }).open) return;
      requestAnimationFrame(() => firstRenderDecision?.(element));
    },
    hostDisconnected() {
      restoreTriggerAfterRemoval(element);
    },
  });
};

/** A dialog or drawer carrying Shoelace's own record of where focus was when it opened. */
type TriggerRecording = HTMLElement & {
  open?: boolean;
  originalTrigger?: Element | null;
};

/**
 * Dialogs whose focus rule 5 decided. Shoelace records where focus was when it opens a dialog
 * (`originalTrigger`) and gives focus back there when the dialog hides, so a person working by
 * keyboard lands where they were rather than at the top of the page; rule 5 does the opening, so
 * it records the same, and these are the dialogs it did it for.
 */
const decidedOnFirstRender = new WeakSet<HTMLElement>();

/**
 * Records the element that held focus before rule 5 moved it, in Shoelace's own `originalTrigger`,
 * so Shoelace's hide path gives it back when the dialog is closed with `open = false` (the quick
 * link's product search). Shoelace overwrites it on any later opening, so it always names the most
 * recent trigger. Nothing is recorded when nothing held focus.
 */
function rememberTrigger(overlay: HTMLElement, target: Document): void {
  const trigger = deepestFocused(target);
  if (!(trigger instanceof HTMLElement) || trigger === target.body) return;
  (overlay as TriggerRecording).originalTrigger = trigger;
  decidedOnFirstRender.add(overlay);
}

/**
 * A dialog rule 5 decided was taken out of the page while still open (the Add Auditor Note
 * dialogs, which their host unmounts rather than closes), so no hide path ran and the browser
 * dropped focus to the body. Give it back to the most recent trigger Shoelace holds, once the
 * removal has settled (Shoelace waits the same way), and only if nothing else has taken focus. A
 * dialog already closed when removed was given its focus back by Shoelace's own hide, and is left.
 */
function restoreTriggerAfterRemoval(overlay: HTMLElement): void {
  if (!decidedOnFirstRender.has(overlay)) return;
  decidedOnFirstRender.delete(overlay);
  const recording = overlay as TriggerRecording;
  const trigger = recording.originalTrigger;
  if (!recording.open || !(trigger instanceof HTMLElement)) return;
  const target = overlay.ownerDocument;
  setTimeout(() => {
    const active = deepestFocused(target);
    const focusLost = active === null || active === target.body;
    if (focusLost && trigger.isConnected) {
      trigger.focus({ preventScroll: true });
    }
  });
}
SlDialog.addInitializer(reportFirstRenderOpen);
SlDrawer.addInitializer(reportFirstRenderOpen);

/**
 * Rule 5: the dialog opened on its first render, so nobody decided its focus. Decide as Shoelace's
 * open path and the listener would have: a field the dialog marks itself, else the first field,
 * else the panel, with rule 4's one bounded wait after the panel for content still rendering.
 *
 * Only a dialog actually on screen is touched: one mounted open inside something hidden (a tab not
 * showing, a collapsed section) would otherwise take the cursor from the page the person is on.
 * Nor is a dialog closed again before the frame came round: its panel stays on screen while its
 * hide animation runs, and a cursor put inside it then would be left in a dialog that is going.
 */
function decideOnFirstRender(
  overlay: HTMLElement,
  target: Document,
  isVisible: VisibilityCheck
): void {
  if (!(overlay as HTMLElement & { open?: boolean }).open) return;
  const panel =
    overlay.shadowRoot?.querySelector<HTMLElement>('[part~="panel"]') ?? null;
  if (!panel || !isVisible(panel)) return;
  rememberTrigger(overlay, target);
  const marked = overlay.querySelector<HTMLElement>('[autofocus]');
  if (marked) {
    marked.focus({ preventScroll: true });
    return;
  }
  const field = findFirstEditableField(overlay, isVisible);
  if (field) {
    field.focus({ preventScroll: true });
    return;
  }
  panel.focus({ preventScroll: true });
  void focusFirstFieldIfDialogStillFocused(overlay, target, isVisible);
}

/**
 * Rule 4: the dialog had no field when it opened, so Shoelace focused its panel. Wait once for its
 * nested Lit elements to finish rendering and one frame more, then put the cursor in the first
 * field, unless the dialog has closed or focus has moved off the dialog since (a click, a keypress,
 * something the dialog focused itself), which is a choice this must not override.
 *
 * The installer calls it once per opening. A dialog whose fields wait on a server answer calls it
 * itself, once, after that answer has rendered (the exception rule 4 records), because the
 * installer's one wait is over by then. Resolves true when it moved the cursor.
 */
export async function focusFirstFieldIfDialogStillFocused(
  overlay: HTMLElement,
  target: Document = document,
  isVisible: VisibilityCheck = isRendered
): Promise<boolean> {
  await Promise.all(pendingUpdates(overlay));
  await nextFrame();
  if (!(overlay as HTMLElement & { open?: boolean }).open) return false;
  if (!focusRestsOnDialog(overlay, target)) return false;
  // A field the dialog marks itself wins here too (rule 2), as it does when it is there on open.
  const field =
    overlay.querySelector<HTMLElement>('[autofocus]') ??
    findFirstEditableField(overlay, isVisible);
  if (!field) return false;
  field.focus({ preventScroll: true });
  return true;
}

/** The innermost focused element, following the active element down through shadow roots. */
function deepestFocused(target: Document): Element | null {
  let active: Element | null = target.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/**
 * Whether focus is still where Shoelace put it on open: on the dialog's panel, on the dialog with
 * nothing inside it focused, or nowhere (the body). Almost every dialog in the app sits inside a
 * component's shadow root, where the document reports only the outermost host as active, so this
 * follows the active element down through each shadow root until it reaches the dialog; focus
 * anywhere off that chain, or on anything inside the dialog but its panel (a field, the close
 * button), is a choice already made.
 */
function focusRestsOnDialog(overlay: HTMLElement, target: Document): boolean {
  let active: Element | null = target.activeElement;
  if (active === null || active === target.body) return true;
  while (active) {
    if (active === overlay) {
      const inner = overlay.shadowRoot?.activeElement ?? null;
      return inner === null || inner.getAttribute('part') === 'panel';
    }
    active = active.shadowRoot?.activeElement ?? null;
  }
  return false;
}
